import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import pluralize from 'pluralize'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { CsvDropzone } from '@/components/forms/CSVUploadForms/CsvDropzone'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { toast } from '@/components/toasts/toast.helpers'
import { uploadCampaignOptOuts } from '@/reducers/outbound/outboundCampaigns.actions'

export const UploadOptOutsModal = ({ campaign, voiceAgentName, onClose, onUploaded }) => {
  const dispatch = useDispatch()
  const loading = useSelector((state) => state.outboundCampaigns.loading)

  const [file, setFile] = useState(null)
  const [errors, setErrors] = useState([])

  const uploading = loading.optOutsUpload

  const handleClose = () => {
    setFile(null)
    setErrors([])
    onClose()
  }

  const handleSubmit = async () => {
    setErrors([])
    const result = await dispatch(uploadCampaignOptOuts(campaign.id, file))

    if (!result.success) {
      setErrors(result.errors)
      return
    }

    const skippedNote = result.skipped ? ` ${result.skipped} already opted out.` : ''
    toast.success(`Added ${pluralize('opt-out', result.created, true)}.${skippedNote}`)
    // The parent owns the refetch; the good rows landed either way.
    onUploaded()

    // Rows the file could not supply are not on the DNC list. Stay open with them
    // listed rather than closing on a toast that only counts what did import.
    if (result.invalid > 0) {
      setFile(null)
      setErrors(result.errors)
      return
    }
    handleClose()
  }

  return (
    <BasicModal
      show
      onClose={handleClose}
      title="Upload opt-outs"
      showCloseButton={!uploading}
      closeOnEscape={!uploading}
      canCloseOnDimmerClick={!uploading}
      data-testid="upload-opt-outs-modal"
      footer={
        <>
          <Button disabled={uploading} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            brand
            disabled={!file}
            loading={uploading}
            onClick={handleSubmit}
            data-testid="upload-opt-outs-submit"
          >
            Upload
          </Button>
        </>
      }
    >
      <div className="mb">
        <p>
          Upload a CSV of numbers to add to the Do Not Call list for{' '}
          <strong>{voiceAgentName || "this campaign's AI agent"}</strong>. They will be skipped on
          every campaign using that agent, not just this one.
        </p>
        <p className="muted-text">
          Required column: <strong>phone_number</strong>. Other columns are ignored, so an exported
          opt-out CSV can be uploaded as-is. Numbers already opted out are skipped, and invalid rows
          are reported without blocking the rest of the file.
        </p>
      </div>
      <CsvDropzone
        file={file}
        onFileSelected={({ file: picked }) => {
          setErrors([])
          setFile(picked)
        }}
        onFileCleared={() => setFile(null)}
        maxSizeMb={10}
        extraHint="up to 50,000 rows"
      />
      {!isEmpty(errors) && (
        <div className="mt" data-testid="upload-opt-outs-errors">
          {errors.map((message) => (
            <ErrorMessage key={message} content={message} />
          ))}
        </div>
      )}
    </BasicModal>
  )
}
